import { defineStore } from "pinia";
import { computed, reactive, ref } from "vue";

import { searchSkilledWorkerSponsors } from "@/api/rolesSkilled";
import { resourceCache } from "@/cache/resourceCache";
import { useUserStore } from "@/stores/user";
import type {
  PagePhase,
  SponsorSearchFilters,
  SponsorSearchResult,
} from "@/types/rolesSkilled";

export const SPONSOR_PAGE_SIZE = 20;
const SPONSOR_CACHE_TTL_MS = 60_000;
const SPONSOR_CACHE_STALE_TTL_MS = 10 * 60_000;

function messageFrom(caught: unknown): string {
  return caught instanceof Error ? caught.message : "Sponsor register search failed.";
}

function stateForResult(payload: SponsorSearchResult): PagePhase {
  if (!payload.has_data) return "no-data";
  return payload.companies.length ? "success" : "no-match";
}

function isAbortError(caught: unknown): boolean {
  return caught instanceof DOMException && caught.name === "AbortError";
}

function filterCacheKey(filters: SponsorSearchFilters): string {
  return [
    filters.limit,
    filters.offset,
    filters.organisation_name,
    filters.town_city,
    filters.rating,
    filters.route,
  ].join("\u0000");
}

export const useSkilledWorkerStore = defineStore("skilled-worker", () => {
  const userStore = useUserStore();
  const filters = reactive({
    organisation_name: "",
    town_city: "",
    rating: "",
    route: "",
  });
  const result = ref<SponsorSearchResult | null>(null);
  const pageSize = ref(SPONSOR_PAGE_SIZE);
  const state = ref<PagePhase>("idle");
  const error = ref("");
  let activeController: AbortController | null = null;
  let generation = 0;

  const page = computed(() => Math.floor((result.value?.offset ?? 0) / pageSize.value) + 1);
  const pageCount = computed(() => {
    const total = result.value?.total_count ?? 0;
    return Math.max(1, Math.ceil(total / pageSize.value));
  });
  const previousDisabled = computed(
    () => state.value === "loading" || state.value === "updating" || (result.value?.offset ?? 0) <= 0,
  );
  const nextDisabled = computed(
    () => state.value === "loading" || state.value === "updating" || !result.value?.has_more,
  );

  function requestFilters(offset: number, requestedPageSize = pageSize.value): SponsorSearchFilters {
    return {
      limit: requestedPageSize,
      offset,
      organisation_name: filters.organisation_name.trim(),
      rating: filters.rating.trim(),
      route: filters.route.trim(),
      town_city: filters.town_city.trim(),
    };
  }

  function applyResult(payload: SponsorSearchResult, requestedPageSize = pageSize.value): void {
    pageSize.value = requestedPageSize;
    result.value = payload;
    state.value = stateForResult(payload);
  }

  async function search(
    offset = 0,
    forceRefresh = false,
    requestedPageSize = pageSize.value,
  ): Promise<void> {
    activeController?.abort();
    const controller = new AbortController();
    activeController = controller;
    const requestGeneration = ++generation;
    state.value = result.value ? "updating" : "loading";
    error.value = "";
    const requestedFilters = requestFilters(offset, requestedPageSize);
    try {
      const scope = userStore.user?.id;
      const cacheKey = `skilled-worker:${filterCacheKey(requestedFilters)}`;
      if (scope && forceRefresh) resourceCache.invalidate(scope, cacheKey);
      const payload = scope
        ? await resourceCache.read({
            key: cacheKey,
            loader: (signal) => searchSkilledWorkerSponsors(requestedFilters, signal),
            onRevalidated: (fresh) => {
              if (generation === requestGeneration && !controller.signal.aborted) {
                applyResult(fresh, requestedPageSize);
              }
            },
            scope,
            signal: controller.signal,
            staleTtlMs: SPONSOR_CACHE_STALE_TTL_MS,
            ttlMs: SPONSOR_CACHE_TTL_MS,
          })
        : await searchSkilledWorkerSponsors(requestedFilters, controller.signal);
      if (activeController !== controller || generation !== requestGeneration || controller.signal.aborted) return;
      applyResult(payload, requestedPageSize);
    } catch (caught: unknown) {
      if (activeController !== controller || generation !== requestGeneration || isAbortError(caught)) return;
      error.value = messageFrom(caught);
      state.value = result.value ? stateForResult(result.value) : "error";
    } finally {
      if (activeController === controller) activeController = null;
    }
  }

  function submitSearch(): void {
    void search(0, true);
  }

  function changePage(direction: -1 | 1): void {
    setPage(page.value + direction);
  }

  function setPage(nextPage: number): void {
    const normalized = Math.min(pageCount.value, Math.max(1, Math.trunc(nextPage)));
    void search((normalized - 1) * pageSize.value);
  }

  function setPageSize(nextPageSize: number): void {
    const normalized = Math.trunc(nextPageSize);
    if (![10, 20, 50].includes(normalized) || normalized === pageSize.value) return;
    void search(0, false, normalized);
  }

  function dispose(): void {
    generation += 1;
    activeController?.abort();
    activeController = null;
    if (state.value === "updating") state.value = result.value ? stateForResult(result.value) : "idle";
  }

  function reset(): void {
    dispose();
    Object.assign(filters, {
      organisation_name: "",
      town_city: "",
      rating: "",
      route: "",
    });
    result.value = null;
    pageSize.value = SPONSOR_PAGE_SIZE;
    state.value = "idle";
    error.value = "";
  }

  return {
    changePage,
    dispose,
    error,
    filters,
    nextDisabled,
    page,
    pageCount,
    pageSize,
    previousDisabled,
    result,
    reset,
    search,
    setPage,
    setPageSize,
    state,
    submitSearch,
  };
});
